import { hasDefinedValue, isDefined } from '@sgme/fp';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import {
  getCouponsErrors,
  getEstimatedComputationTimeInSeconds,
  getOptimizationStartDate,
  getUnderlyingTags,
  isAdvisoryProcessing,
} from 'state/advisory/basket-optimizer/selectors';
import {
  basketOptimizerOptimizeCanceledThunk,
  basketOptimizerOptimizeRequestedThunk,
} from 'state/advisory/basket-optimizer/thunks';
import { getFormProductType, getQuoteUnderlyings, isQuoteConsistentForAdvisory } from 'state/selectors';
import { getTabQuoteWorkspaceType } from 'state/workspaces/workspaces/workspaces.selector';

const hasDefinedRawValue = hasDefinedValue('rawValue');

export function useOptimizationState(tabId: string) {
  const underlyings = useAppSelector(s => getQuoteUnderlyings(s, tabId))!;
  const workspaceType = useAppSelector(s => getTabQuoteWorkspaceType(s, tabId));
  const underlyingTags = useAppSelector(s => getUnderlyingTags(s, tabId) || undefined);
  const isOptimizing = useAppSelector(s => isAdvisoryProcessing(s, tabId));
  const validUnderlyingTagCount = underlyingTags?.filter(hasDefinedRawValue).length ?? 0;
  const couponErrors = useAppSelector(state => getCouponsErrors(state, tabId));
  const isQuoteConsistent = useAppSelector(
    s => isDefined(workspaceType) && isQuoteConsistentForAdvisory(s, workspaceType, tabId),
  );
  const isUnderlyingTagsValidForOptimizing = underlyings && underlyings.length <= validUnderlyingTagCount;
  const canOptimize =
    !isOptimizing && isUnderlyingTagsValidForOptimizing && !couponErrors.hasError() && isQuoteConsistent;
  return {
    isOptimizing,
    canOptimize,
    optimizationStartDate: useAppSelector(s => getOptimizationStartDate(s, tabId)),
    estimatedComputationTimeInSeconds: useAppSelector(s => getEstimatedComputationTimeInSeconds(s, tabId)),
  };
}

export function useOptimizationWorkflow(tabId: string) {
  const dispatch = useThunkDispatch();
  const underlyings = useAppSelector(s => getQuoteUnderlyings(s, tabId))!;
  const productType = useAppSelector(s => getFormProductType(s, tabId));
  return {
    onOptimizeRequested() {
      dispatch(basketOptimizerOptimizeRequestedThunk(tabId, productType, underlyings.length));
      logAnalytics.trackEvent('SG Advisory', 'Start optimization');
    },
    onOptimizeCancelled() {
      dispatch(basketOptimizerOptimizeCanceledThunk(tabId));
      logAnalytics.trackEvent('SG Advisory', 'Cancel optimization');
    },
  };
}
